import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Clock,
  UserPlus,
  ChatCircle,
  ArrowRight,
  Pulse,
  Sparkle,
} from '@phosphor-icons/react';

const API = `/api`;

const LeadTimeline = ({ leadId }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (leadId) fetchTimeline();
  }, [leadId]);

  const fetchTimeline = async () => {
    try {
      const { data } = await axios.get(`${API}/leads/${leadId}/timeline`, {
        withCredentials: true,
      });
      setTimeline(data.timeline || []);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'creation':
        return { icon: UserPlus, color: 'bg-blue-100 text-blue-600' };
      case 'ai_qualification':
        return { icon: Sparkle, color: 'bg-purple-100 text-purple-600' };
      case 'status_change':
        return { icon: Pulse, color: 'bg-orange-100 text-orange-600' };
      case 'assignment':
        return { icon: ArrowRight, color: 'bg-emerald-100 text-emerald-600' };
      case 'conversation':
        return { icon: ChatCircle, color: 'bg-cyan-100 text-cyan-600' };
      default:
        return { icon: Clock, color: 'bg-slate-100 text-slate-600' };
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock size={32} className="mx-auto text-slate-300 mb-3" />
        <p className="text-sm text-slate-500">Nenhum evento registrado ainda</p>
      </div>
    );
  }

  return (
    <div className="relative space-y-4" data-testid="lead-timeline">
      <div className="absolute left-5 top-2 bottom-2 w-px bg-slate-200"></div>

      {timeline.map((event, index) => {
        const { icon: Icon, color } = getIcon(event.type);
        return (
          <div
            key={index}
            className="relative flex gap-4 items-start"
            data-testid={`timeline-event-${index}`}
          >
            <div
              className={`relative z-10 w-10 h-10 rounded-full ${color} flex items-center justify-center flex-shrink-0`}
            >
              <Icon size={18} weight="duotone" />
            </div>
            <div className="flex-1 bg-white border border-slate-200 rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-sm text-slate-900">{event.title}</p>
                <span className="text-xs text-slate-400 flex-shrink-0">
                  {formatDate(event.timestamp)}
                </span>
              </div>
              {event.description && (
                <p className="text-xs text-slate-600 mt-1">{event.description}</p>
              )}
              {event.user_name && (
                <p className="text-xs text-slate-400 mt-2">por {event.user_name}</p>
              )}
              {event.metadata && Object.keys(event.metadata).length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100 grid grid-cols-2 gap-1 text-xs">
                  {event.metadata.intencao_compra && (
                    <p className="text-slate-600">
                      <span className="font-medium">Intenção:</span> {event.metadata.intencao_compra}
                    </p>
                  )}
                  {event.metadata.faixa_valor && (
                    <p className="text-slate-600">
                      <span className="font-medium">Valor:</span> {event.metadata.faixa_valor}
                    </p>
                  )}
                  {event.metadata.prazo_compra && (
                    <p className="text-slate-600">
                      <span className="font-medium">Prazo:</span> {event.metadata.prazo_compra}
                    </p>
                  )}
                  {event.metadata.regiao_interesse && (
                    <p className="text-slate-600">
                      <span className="font-medium">Região:</span> {event.metadata.regiao_interesse}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LeadTimeline;
